import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, MessageSquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import EmptyState from 'components/ui/EmptyState';
import { useChatStore, ChatMessage as ChatMessageType } from 'store/chat.store';
import ChatSkeleton from './ChatSkeleton';
import ChatSubmit from './ChatSubmit';
import ChatMessages from './ChatMessages';
import CustomChatLoading from './CustomChatLoading';
import * as Learning from 'modules/learning';
import { nanoid } from 'nanoid';
import config from 'config';
import { useToast } from 'components/ui/use-toast';
import { useAuthContext } from 'modules/auth';
import { useChatTiers } from 'modules/llm';
import { buildChatRequestBody } from './chatRequest';
interface ChatsProps {
    /** The notebook this chat belongs to. A notebook has ONE conversation; it is the history key. */
    notebookId: string;
    /** Retrieval scope sent to the backend: the checked sources of the notebook (required, 1..25). */
    documentIds: string[];
    documentTitle?: string;
    className?: string;
    isFullWidth?: boolean;
    /** When false, sending is blocked and a progress banner is shown. */
    isReady?: boolean;
    /** Localized stage label shown in the gating banner while !isReady. */
    processingLabel?: string;
}
const SUGGESTION_CHIP_KEYS = [
    'summarize_this_document',
    'what_are_the_key_concepts',
    'explain_the_main_ideas',
    'quiz_me_on_this_topic',
] as const;
export const Chats: React.FC<ChatsProps> = ({ notebookId, documentIds, documentTitle, className, isFullWidth, isReady = true, processingLabel, }) => {
    const { t } = useTranslation();
    const chatContainerRef = useRef<HTMLDivElement | null>(null);
    const [userHasScrolled, setUserHasScrolled] = useState(false);
    const isManualScrolling = useRef(false);
    const [isLoadingHistory, setIsLoadingHistory] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const abortControllerRef = useRef<AbortController | null>(null);
    const { toast } = useToast();
    const { state: authState } = useAuthContext();
    const { getMessages, addMessage, updateMessage, setMessages, isStreaming, setIsStreaming, highlightedText, setHighlightedText, selectedTier, setSelectedTier, } = useChatStore();

    const handleStop = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
        setIsStreaming(false);
        setIsSubmitting(false);
    }, [setIsStreaming]);

    // Abort any ongoing stream on unmount
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
                abortControllerRef.current = null;
            }
        };
    }, []);
    const { unlockedTiers } = useChatTiers();
    // Resolve to a tier the org's plan actually grants. Null while the ladder is
    // loading or when the plan grants none — send is gated on this so we never
    // ship a stale persisted choice the backend will reject.
    const activeTier = unlockedTiers.find((tier) => tier.key === selectedTier)?.key
        ?? unlockedTiers[0]?.key
        ?? null;
    // Once the ladder loads, normalize a stale or unset selection so the picker,
    // the store, and the next request all agree.
    useEffect(() => {
        if (unlockedTiers.length === 0) return;
        if (activeTier && activeTier !== selectedTier) {
            setSelectedTier(activeTier);
        }
    }, [activeTier, selectedTier, unlockedTiers.length, setSelectedTier]);
    const messages = getMessages(notebookId);
    // Load chat history on mount
    useEffect(() => {
        const loadChatHistory = async () => {
            try {
                setIsLoadingHistory(true);
                const { data: response } = await Learning.Api.GetChatHistory(notebookId) as any;
                if (response.success && response.data) {
                    // API returns messages sorted by createdAt descending, reverse for chronological order
                    const formattedMessages: ChatMessageType[] = response.data
                        .reverse()
                        .map((msg: any) => ({
                            id: msg._id,
                            role: msg.role,
                            content: msg.content,
                            quoteText: msg.quoteText,
                            imageUrls: msg.imageUrls,
                            sources: msg.sources,
                            createdAt: typeof msg.createdAt === 'string' ? new Date(msg.createdAt) : msg.createdAt,
                            isStreaming: msg.isStreaming || false,
                        }));
                    setMessages(notebookId, formattedMessages);
                }
            }
            catch (error) {
                console.error('Failed to load chat history:', error);
                // Don't show error to user, just start with empty chat
            }
            finally {
                setIsLoadingHistory(false);
            }
        };
        if (notebookId) {
            loadChatHistory();
        }
    }, [notebookId, setMessages]);
    // Handle scroll detection
    const handleScroll = useCallback(() => {
        const currentRef = chatContainerRef.current;
        if (currentRef) {
            isManualScrolling.current = true;
            const { scrollTop, clientHeight, scrollHeight } = currentRef;
            const atBottom = scrollTop + clientHeight >= scrollHeight - 50;
            setUserHasScrolled(!atBottom);
            setTimeout(() => {
                isManualScrolling.current = false;
            }, 250);
        }
    }, []);
    useEffect(() => {
        const currentRef = chatContainerRef.current;
        currentRef?.addEventListener('scroll', handleScroll);
        return () => {
            currentRef?.removeEventListener('scroll', handleScroll);
        };
    }, [handleScroll]);
    // Auto-scroll to bottom when new messages arrive (only if user hasn't scrolled up)
    useEffect(() => {
        if (isManualScrolling.current || userHasScrolled)
            return;
        const currentRef = chatContainerRef.current;
        if (currentRef && !userHasScrolled) {
            const scrollToBottom = () => {
                if (currentRef.scrollHeight > currentRef.clientHeight) {
                    currentRef.scrollTop = currentRef.scrollHeight;
                }
            };
            const animationFrame = requestAnimationFrame(scrollToBottom);
            return () => {
                cancelAnimationFrame(animationFrame);
            };
        }
    }, [messages, userHasScrolled]);
    // Auto-scroll during streaming
    useEffect(() => {
        if (!isStreaming || isManualScrolling.current || userHasScrolled)
            return;
        const currentRef = chatContainerRef.current;
        if (!currentRef)
            return;
        const scrollToBottom = () => {
            if (currentRef.scrollHeight > currentRef.clientHeight) {
                currentRef.scrollTop = currentRef.scrollHeight;
            }
        };
        const interval = setInterval(scrollToBottom, 100);
        return () => clearInterval(interval);
    }, [isStreaming, userHasScrolled]);
    const handleSubmit = useCallback(async (text: string) => {
        if (isSubmitting)
            return;
        // Guard: document must be fully processed — chat answers rely on the
        // indexed chunks, so sending before that produces context-free replies.
        if (!isReady)
            return;
        // Guard: the notebook id keys the conversation and at least one source must be picked
        if (!notebookId?.trim() || !documentIds?.length) {
            console.error('Chat requires a notebook ID and at least one source');
            return;
        }
        const trimmedText = typeof text === 'string' ? text.trim() : '';
        if (!trimmedText) {
            return;
        }
        // Guard: refuse to send if the org's plan grants no AI tier at all —
        // the backend would reject it anyway.
        if (!activeTier) {
            toast.error(t("no_models_available"), {
                description: t("contact_admin_to_enable_a_model"),
            });
            return;
        }
        setIsSubmitting(true);
        // Add user message
        const userMessage: ChatMessageType = {
            id: nanoid(),
            role: 'user',
            content: trimmedText,
            quoteText: highlightedText || undefined,
            createdAt: new Date(),
        };
        addMessage(notebookId, userMessage);
        // Add placeholder for assistant response
        const assistantMessageId = nanoid();
        const assistantMessage: ChatMessageType = {
            id: assistantMessageId,
            role: 'assistant',
            content: '',
            isStreaming: true,
            createdAt: new Date(),
        };
        addMessage(notebookId, assistantMessage);
        setIsStreaming(true);
        // Reset scroll state if user was at bottom
        const currentRef = chatContainerRef.current;
        if (currentRef) {
            const { scrollTop, clientHeight, scrollHeight } = currentRef;
            const atBottom = scrollTop + clientHeight >= scrollHeight - 50;
            if (atBottom) {
                setUserHasScrolled(false);
            }
        }
        // Clear highlight after sending
        setHighlightedText(null);
        const controller = new AbortController();
        abortControllerRef.current = controller;
        let fullContent = '';
        try {
            // Get auth token (stored directly under 'accessToken' key by bootstrap.ts)
            const accessToken = localStorage.getItem(config.api.access_token_key);
            const storedOrgId = localStorage.getItem(config.api.active_org_key);
            const activeOrgId = storedOrgId || authState.organization?.id || null;
            if (!storedOrgId && authState.organization?.id) {
                localStorage.setItem(config.api.active_org_key, authState.organization.id);
            }
            const response = await fetch(`${config.api.base_url}/api/chat`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
                    ...(activeOrgId && { 'X-Organization-Id': activeOrgId }),
                },
                signal: controller.signal,
                body: JSON.stringify(buildChatRequestBody({
                    notebookId,
                    // Retrieval scope: the checked sources (RAG spans them all).
                    documentIds,
                    query: trimmedText,
                    quoteText: highlightedText,
                    chatTier: activeTier,
                    documentTitle,
                    organizationId: activeOrgId,
                })),
            });
            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(errorText || 'Failed to get response');
            }
            if (!response.body) {
                throw new Error('No response body');
            }
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            while (true) {
                const { done, value } = await reader.read();
                if (done)
                    break;
                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                // Keep the last incomplete line in buffer
                buffer = lines.pop() || '';
                for (const line of lines) {
                    if (!line.trim())
                        continue;
                    // Handle SSE format: lines starting with "data: "
                    if (line.startsWith('data: ')) {
                        const jsonStr = line.slice(6).trim();
                        if (!jsonStr)
                            continue;
                        let parsed: any | null = null;
                        try {
                            parsed = JSON.parse(jsonStr);
                        }
                        catch {
                            parsed = null;
                        }

                        if (!parsed) {
                            // Fallback: try parsing as raw JSON (non-SSE format)
                            try {
                                const rawData = JSON.parse(jsonStr);
                                if (rawData.content) {
                                    fullContent += rawData.content;
                                    updateMessage(notebookId, assistantMessageId, fullContent);
                                }
                            }
                            catch {
                                console.warn('Failed to parse chunk:', jsonStr);
                            }
                            continue;
                        }

                        if (parsed.type === 'response') {
                            fullContent += parsed.content || '';
                            updateMessage(notebookId, assistantMessageId, fullContent);
                        }
                        else if (parsed.type === 'done') {
                            // Response complete
                        }
                        else if (parsed.type === 'error') {
                            const err: any = new Error(parsed.message || 'An error occurred');
                            if (parsed.code) {
                                err.code = parsed.code;
                            }
                            throw err;
                        }
                    }
                    else {
                        // Try parsing as raw JSON (fallback for non-SSE format)
                        try {
                            const data = JSON.parse(line);
                            if (data.type === 'response' && data.content) {
                                fullContent += data.content;
                                updateMessage(notebookId, assistantMessageId, fullContent);
                            }
                        }
                        catch {
                            // Ignore parse errors for incomplete chunks
                        }
                    }
                }
            }
            // Process any remaining buffer
            if (buffer.trim()) {
                try {
                    const data = JSON.parse(buffer);
                    if (data.type === 'response' && data.content) {
                        fullContent += data.content;
                        updateMessage(notebookId, assistantMessageId, fullContent);
                    }
                }
                catch {
                    // Ignore parse errors
                }
            }
            // Final update
            updateMessage(notebookId, assistantMessageId, fullContent || 'Sorry, I could not generate a response.');
        }
        catch (error: any) {
            if (error?.name === 'AbortError' || controller.signal.aborted) {
                // Stopped intentionally by user — keep streamed content without showing errors
                const stoppedText = fullContent.trim() || t("response_stopped");
                updateMessage(notebookId, assistantMessageId, stoppedText);
                return;
            }
            console.error('Chat error:', error);
            if (error?.code === 'USAGE_LIMIT_EXCEEDED') {
                toast.error(
                    'You are out of tokens , for more tokens please upgrade your plan or contact with Support',
                );
            }
            else if (error?.code === 'FEATURE_NOT_ENTITLED') {
                toast.error(
                    'AI chat is not available on your current plan. Please upgrade your plan or contact Support.',
                );
            }
            // Backend errors arrive with a stable code and an already-formalized,
            // user-facing message — show it as-is. Transport/network failures have
            // no code, so fall back to a generic line instead of leaking a raw error.
            updateMessage(notebookId, assistantMessageId, error?.code
                ? (error.message || 'Sorry, there was an error processing your request. Please try again.')
                : 'Sorry, there was an error reaching the AI assistant. Please try again.');
        }
        finally {
            if (abortControllerRef.current === controller) {
                abortControllerRef.current = null;
            }
            setIsStreaming(false);
            setIsSubmitting(false);
        }
    }, [
        notebookId,
        documentIds,
        documentTitle,
        addMessage,
        updateMessage,
        setIsStreaming,
        isSubmitting,
        isReady,
        highlightedText,
        setHighlightedText,
        activeTier,
        authState.organization?.id,
        toast,
        t,
    ]);
    if (isLoadingHistory) {
        return <ChatSkeleton />;
    }
    const isChatEmpty = !messages || messages.length === 0;
    // A neutral empty state (grey icon circle, title, one muted line) and plain
    // suggestion chips that send their text as the first question.
    const renderChatEmpty = () => (<div className="flex flex-col items-center gap-4 py-6">
        <EmptyState className="p-0" icon={<MessageSquare />} title={t("document_ai_tutor")} description={t("ai_chat_empty_hint")} />
        <div className="flex max-w-[440px] flex-wrap justify-center gap-2">
            {SUGGESTION_CHIP_KEYS.map((key) => (<button key={key} type="button" onClick={() => handleSubmit(t(key))} disabled={!isReady} className="h-10 rounded-md border border-border bg-card px-3 text-[13px] text-foreground/80 transition-colors hover:border-foreground/20 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:h-8">
                {t(key)}
            </button>))}
        </div>
    </div>);
    // Fill the slot the parent gives us — never assert a viewport-derived height.
    // The only consumer (NotebookWorkspace) already sizes the chat column and
    // clips it with `overflow-hidden`, so any hardcoded `100vh - X` here
    // overshoots the slot and pushes the composer below the visible area.
    // No header of its own: the workspace's panel head ("Chat · 2 ta manba") is the one.
    return (<div key="chats-container" className={cn('w-full h-full min-h-0 flex flex-col', className)}>
        {/* Messages area */}
        <div key="chat-messages" ref={chatContainerRef} onScroll={handleScroll} className="flex-1 overflow-y-auto overscroll-y-none flex flex-col min-h-0 scrollbar-thin">
            <div className={cn('max-w-full mx-auto flex-grow flex flex-col p-5', isFullWidth ? 'lg:w-3/5 2xl:w-1/2' : 'w-full', isChatEmpty ? 'justify-center' : 'justify-start')}>
                {isChatEmpty ? (renderChatEmpty()) : (<>
                    {/* An even 20px between messages: `space-y-5` replaces the list's
                        own spacing and `[&>div]:mb-0` drops each item's bottom margin. */}
                    <ChatMessages messages={messages} className="space-y-5 [&>div]:mb-0" />
                    <CustomChatLoading
                        visible={isStreaming && messages[messages.length - 1]?.role === 'assistant' && !messages[messages.length - 1]?.content}
                    />
                </>)}
            </div>
        </div>

        {/* Composer — its send button turns into the only stop button while an answer streams. */}
        <div key="chat-input-container" className={cn('relative flex-shrink-0 w-full max-w-full mx-auto border-t border-border px-4 pb-3.5 pt-3', isFullWidth ? 'lg:w-3/5 2xl:w-1/2' : 'w-full')}>
            {/* Soft edge so the last line of an answer fades under the composer instead of being cut. */}
            <div aria-hidden className="pointer-events-none absolute inset-x-0 -top-6 h-6 bg-gradient-to-t from-background to-transparent"/>
            {!isReady && (<div className="mb-2 flex items-center gap-2 rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 flex-shrink-0 animate-spin" />
                <span>
                    {processingLabel ? `${processingLabel} · ` : ''}{t("chat_unlocks_when_processing_completes")}
                </span>
            </div>)}
            <ChatSubmit
                key="chat-submit"
                isSubmitting={isSubmitting}
                handleSubmit={handleSubmit}
                onStop={handleStop}
                notebookId={notebookId}
                disabled={!isReady}
            />
        </div>
    </div>);
};
export default Chats;
